<template>
  <div>
    <FormSelect
      name="childCommodity"
      :items="compChildCommodities"
      :emptyFirstOption="false"
      v-model="search.childCommodity"
      @change="childCommodityChange"
    >
      <template #label>
        <FormLabel class="font-medium -mt-2">{{ $t('formFields.category') }}</FormLabel>
      </template>
    </FormSelect>

    <UtilDivider />

    <!-- ORDER BY -->
    <FormSelect
      name="orderBy"
      :value="search.index"
      @change="changeOrderBy"
      :items="compOrderBy"
      :emptyFirstOption="false"
    >
      <template #label>
        <FormLabel class="font-medium -mt-2">{{ $t('formFields.orderBy') }}</FormLabel>
      </template>
    </FormSelect>

    <UtilSearchGeo />

    <UtilSearchFacetSingleToggle facetKey="tradeType" label="Wanted" onValue="Wanted" />

    <UtilSearchFacetSingleToggle facetKey="status" label="Hide Sold" :onValue="listingStatus.LIVE" />

    <UtilSearchFacetSingleToggle
      v-if="searchIs.livestock"
      facetKey="meta.saleDate"
      label="Hide Auctions"
      :onValue="false"
    />

    <UtilSearchFacetSingleToggle
      v-if="searchIs.cattle"
      facetKey="meta.buyNow"
      label="Buy Now"
      :onValue="buyNowStatus.ENABLED"
    />

    <!-- SPECIES -->
    <template v-if="(searchIs.livestock && !search.childCommodity) || searchIs.feedBedding">
      <UtilSearchFacet facetKey="categories.lvl0" :search="search" v-slot="{ totalItems }">
        <template v-if="totalItems.length > 0">
          <UtilDivider />

          <h5 class="font-medium mb-1">{{ searchIs.feedBedding ? $t('search.groups') : $t('search.species') }}</h5>
        </template>
      </UtilSearchFacet>
    </template>

    <!-- TYPES -->
    <template v-if="searchIs.livestock && !search.childCommodity">
      <UtilSearchFacet facetKey="meta.types" :search="search" v-slot="{ totalItems }">
        <template v-if="totalItems.length > 0">
          <UtilDivider />

          <h5 class="font-medium mb-1">{{ $t('search.types') }}</h5>
        </template>
      </UtilSearchFacet>
    </template>

    <!-- BREEDS -->
    <template v-if="searchIs.livestock">
      <UtilSearchFacet
        :search="search"
        facetKey="categories.lvl1"
        :maxVisible="5"
        hierarchical
        searchable
        searchPlaceholder="Search Breeds"
        v-slot="{ totalItems }"
      >
        <template v-if="totalItems.length > 0">
          <UtilDivider />

          <h5 class="font-medium mb-1">{{ $t('search.breeds') }}</h5>
        </template>
      </UtilSearchFacet>
    </template>

    <!-- SEXES -->
    <template v-if="searchIs.livestock">
      <UtilSearchFacet :search="search" facetKey="meta.sexes" v-slot="{ totalItems }">
        <template v-if="totalItems.length > 0">
          <UtilDivider />

          <h5 class="font-medium mb-1">{{ $t('search.sexes') }}</h5>
        </template>
      </UtilSearchFacet>
    </template>

    <!-- PURPOSE -->
    <template v-if="searchIs.livestock && !searchIs.cattle">
      <UtilSearchFacet :search="search" facetKey="meta.types" v-slot="{ totalItems }">
        <template v-if="totalItems.length > 0">
          <UtilDivider />

          <h5 class="font-medium mb-1">{{ $t('formFields.purposeType') }}</h5>
        </template>
      </UtilSearchFacet>
    </template>

    <!-- HEALTH -->
    <template v-if="searchIs.livestock && searchIs.cattle || searchIs.livestock && searchIs.sheep">
      <UtilDivider />

      <h5 class="font-medium mb-1">{{ $t('titles.health') }}</h5>

      <!-- TB INTERVAL -->
      <UtilDropdownFacet
        v-if="searchIs.cattle"
        :options="tbIntervalOptions"
        :search="search"
        facetKey="meta.tbInterval"
        name="tb-interval"
        label="TB Interval"
      />

      <UtilSearchFacetSingleToggle
        v-if="searchIs.cattle"
        facetKey="meta.tbRestricted"
        label="TB Restricted"
        :onValue="true"
      />

      <UtilSearchFacetSingleToggle
        v-if="searchIs.breeding || (searchIs.sheep && searchIs.store)"
        facetKey="meta.premiumHealthScheme"
        label="Premium Health Scheme"
        :onValue="true"
      />

      <UtilSearchFacetSingleToggle
        v-if="searchIs.sheep"
        facetKey="meta.health.Maedi Visna.accredited"
        label="MV Accredited"
        :onValue="true"
      />

      <UtilSearchFacetSingleToggle
        v-if="searchIs.sheep"
        facetKey="meta.health.Scrapie.accredited"
        label="Scrapie Monitored"
        :onValue="true"
      />
    </template>

    <!-- OTHER -->
    <template v-if="Object.keys(compAdditionalFacets).length > 0">
      <UtilDivider />

      <h5 class="font-medium mb-1">{{ $t('search.other') }}</h5>

      <div v-for="facet in compAdditionalFacets" :key="facet.facetKey">
        <UtilSearchFacetSingleToggle
          v-if="!facet.hideFromAutomaticSidebarPosition"
          :facetKey="facet.facetKey"
          :label="facet.label"
          :onValue="facet.onValue"
        />
      </div>
    </template>

    <UtilDivider />

    <!-- SAVED SEARCH -->
    <!-- <h5 class="font-medium mb-1">{{ $t('search.savedSearches') }}</h5>

    <p v-if="!savedSearches.length" class="text-sm mb-2">
      {{ $t('search.quicklyFindWayBack') }}
    </p>

    <div
      v-for="savedSearch in savedSearches"
      :key="savedSearch.preferenceRef"
      class="flex items-center justify-between mt-1 mb-1"
    >
      <UtilTextLink :to="savedSearch.url" @click="$scroll.toTop()" class="block text-brand truncate mr-2">
        <FontAwesomeIcon :icon="faBookmark" class="mr-2" />

        <span>{{ savedSearch.name }}</span>
      </UtilTextLink>

      <UtilButton
        @click="deleteSavedSearch(savedSearch)"
        :loading="preferenceApi.loading && preferenceApi.model.preferenceRef === savedSearch.preferenceRef"
        textLink
        class="text-gray hover:text-gray-darker py-0"
      >
        <FontAwesomeIcon :icon="faTrashAlt" />
      </UtilButton>
    </div>

    <UtilButton @click="saveSearch()" color="secondary" small class="mt-1">
      <FontAwesomeIcon :icon="faBookmark" fixed-width />
      <span>{{ $t('search.saveSearch') }}</span>
    </UtilButton>

    <UtilDivider /> -->

    <UtilButton
      v-show="Object.keys(activeFilters).length > 0 && $breakpoint.lgAndUp"
      @click="resetFilters()"
      textLink
      class="text-brand mt-4"
    >
      <FontAwesomeIcon :icon="faTimes" />
      {{ $t('buttons.resetFilters') }}
    </UtilButton>
  </div>
</template>

<script>
  import {faBookmark, faSearch, faTimes, faTrashAlt} from '@fortawesome/pro-regular-svg-icons'
  import {mapGetters, mapState} from 'vuex'
  import LegacyApi from '@/lib/api/legacy'
  import getAdditionalFacets from '@/lib/mapping/search-additional-facets'
  import listingStatus from '@/lib/mapping/listing-status'
  import {buyNowStatus} from '@/lib/mapping'
  import commodities from '@/lib/helpers/commodities'
  import FormSelect from '@/components/forms/elements/FormSelect'
  import FormLabel from '@/components/forms/elements/FormLabel'
  import UtilDivider from '@/components/utils/UtilDivider'
  import UtilButton from '@/components/utils/UtilButton'
  import UtilSearchFacet from '@/components/utils/search/UtilSearchFacet'
  import UtilSearchFacetSingleToggle from '@/components/utils/search/UtilSearchFacetSingleToggle'
  import UtilSearchGeo from '@/components/utils/search/UtilSearchGeo'
  import searchOrder from '@/lib/mapping/search-order'
  import tbIntervalOptions from '@/lib/options/tb-interval'
  import UtilDropdownFacet from '@/components/utils/search/UtilDropdownFacet'

  export default {
  name: 'SearchFilters',
  components: {
    FormSelect,
    FormLabel,
    UtilDivider,
    UtilButton,
    UtilSearchFacet,
    UtilSearchFacetSingleToggle,
    UtilSearchGeo,
    UtilDropdownFacet
  },
  props: {
    commodity: Object
  },
  data() {
    return {
      preferenceApi: new LegacyApi(),
      breedSearch: '',
      listingStatus,
      buyNowStatus,
      faTimes,
      faBookmark,
      faSearch,
      faTrashAlt,
      tbIntervalOptions
    }
  },
  computed: {
    ...mapState({
      search: state => state.search
    }),

    ...mapGetters({
      searchIs: 'search/searchIs',
      activeFilters: 'search/activeFilters',
      savedSearches: 'storage/savedSearches'
    }),

    compChildCommodities() {
      let childCommodities = [
        {
          text: 'All Categories',
          value: ''
        }
      ]

      commodities.forEach(commodity => {
        if (commodity.slug === 'livestock') return

        childCommodities.push({
          text: commodity.name,
          value: commodity.slug
        })
      })

      return childCommodities
    },

    compAdditionalFacets() {
      return getAdditionalFacets(this.commodity, this.searchIs, false)
    },

    compOrderBy() {
      return Object.values(searchOrder)
    }
  },
  methods: {
    childCommodityChange(childCommodity) {
      this.$router.push(`/search/${childCommodity}`).catch(() => {})
    },

    changeOrderBy(index) {
      this.$store.commit('search/mutate', { key: 'index', value: index })
      this.$store.dispatch('search/applyFilters')

      this.$analytics.addEvent({
        category: 'search',
        action: 'Changed order by',
        label: index
      })
    },

    resetFilters() {
      this.$analytics.addEvent({
        category: 'search',
        action: 'Reset filters'
      })

      this.$store.dispatch('search/reset')

      this.$scroll.toTop()
    },

    saveSearch() {
      if (!this.appIs.payingUser) {
        return this.$store.dispatch('payments/showSubscriptionPlanChooser', {
          origin: 'listings search'
        })
      }

      let categories = []

      if (this.search.refinementList) {
        if (this.search.refinementList['categories.lvl0']) {
          this.search.refinementList['categories.lvl0'].forEach(algoliaCategoryName =>
            categories.push(algoliaCategoryName)
          )
        }

        if (this.search.refinementList['categories.lvl1']) {
          this.search.refinementList['categories.lvl1'].forEach(algoliaCategoryName =>
            categories.push(algoliaCategoryName)
          )
        }
      }

      categories = categories.map(algoliaCategoryName =>
        this.$store.getters['categories/getCategorySlugFromAlgolia'](this.search.commodity, algoliaCategoryName)
      )

      this.$store.dispatch('modal/showModal', {
        modalName: 'ModalSearchSave',
        modalData: { searchUrl: this.search.url, categories: categories, commodity: this.search.commodity }
      })
    },

    async deleteSavedSearch(savedSearch) {
      // There is no preferences.delete so let's just turn off the email alert :/
      await this.preferenceApi.post('preferences.save', {
        preferenceRef: savedSearch.preferenceRef,
        commodity: savedSearch.commodity,
        alert: 0,
        buying: {
          categories: savedSearch.categories
        }
      })

      await this.$store.dispatch('storage/save', {
        key: 'savedSearches',
        value: this.savedSearches.filter(search => search.preferenceRef !== savedSearch.preferenceRef)
      })

      this.$analytics.addEvent({
        category: 'search',
        action: 'Deleted saved search'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.breed-filter {
  max-width: 180px;
}
</style>
